import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { formatPrice, calculateDiscount } from '../utils/formatPrice';
import { getProductImage } from '../utils/imageHelper';
import { getProductDetails } from '../data/productDetails';
import products from '../data/products.json';
import { ProductCard } from '../components/ui/ProductCard';
import { BadgeGroup } from '../components/ui/Badge';
import { ProductTabs } from '../components/product/ProductTabs';
import { usePageSEO } from '../hooks/usePageSEO';
import {
  ChevronRight,
  Star,
  Plus,
  Minus,
  ShoppingCart,
  Check,
  ShieldCheck,
  Truck,
  Zap,
  PackageX,
  Layers,
  Heart,
  Share2,
  ArrowLeft,
} from 'lucide-react';

const WHATSAPP_PHONE = '923056367673';

const TRUST_PILLS = [
  { icon: '🌿', label: '100% Pure Herbal' },
  { icon: '🧪', label: 'Lab Tested' },
  { icon: '🛡️', label: 'Steroid Free' },
  { icon: '☪️', label: 'Halal Certified' },
];

function StarRating({ rating = 5, reviewsCount = 0 }) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.5;
  const emptyStars = Math.max(0, 5 - fullStars - (hasHalf ? 1 : 0));

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5" aria-label={`Rating: ${rating} out of 5 stars`}>
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`f-${i}`} className="w-4 h-4 text-amber-400 fill-amber-400" />
        ))}
        {hasHalf && (
          <div className="relative">
            <Star className="w-4 h-4 text-gray-200 fill-gray-200" />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            </div>
          </div>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`e-${i}`} className="w-4 h-4 text-gray-200 fill-gray-200" />
        ))}
      </div>
      <span className="text-sm font-semibold text-gray-900">{rating.toFixed(1)}</span>
      <span className="text-xs text-gray-500">({reviewsCount} reviews)</span>
      <span className="hidden sm:inline-block text-xs bg-emerald-50 text-emerald-700 font-medium px-2 py-0.5 rounded-md border border-emerald-200/60">
        Verified Herbal Care
      </span>
    </div>
  );
}

export function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const imageContainerRef = useRef(null);

  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // 1. Data Resolution
  const product = useMemo(() => {
    return products.find((p) => p.id === Number(id));
  }, [id]);

  const details = useMemo(() => {
    return product ? getProductDetails(product.id) : null;
  }, [product]);

  // Dynamic SEO & Structured Data (Product + BreadcrumbList Schema.org)
  const seoData = useMemo(() => {
    if (!product) {
      return {
        title: 'Product Not Found | WellZora Herbal Care',
        description: 'The requested herbal product could not be found.',
        canonical: 'https://wellzoraherbalcare.com/shop',
      };
    }

    const title = `${product.name}${product.size ? ` (${product.size})` : ''} - Price in Pakistan | WellZora`;
    const description = `${product.description} 100% natural herbal formula by WellZora Herbal Care. DRAP approved, laboratory tested. Rs. ${product.price}. Cash on delivery across Pakistan.`;
    const canonical = `https://wellzoraherbalcare.com/product/${product.id}`;
    const productImageUrl = 'https://wellzoraherbalcare.com/assets/Logo.jpg';

    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Product',
          '@id': `https://wellzoraherbalcare.com/product/${product.id}#product`,
          name: product.name,
          alternateName: product.nameUrdu,
          description: product.description || details?.overview?.en || '100% pure herbal formulation by WellZora Herbal Care.',
          image: productImageUrl,
          sku: `WZ-${String(product.id).padStart(3, '0')}`,
          brand: {
            '@type': 'Brand',
            name: 'WellZora Herbal Care',
          },
          category: product.category,
          offers: {
            '@type': 'Offer',
            url: canonical,
            priceCurrency: 'PKR',
            price: String(product.price),
            priceValidUntil: '2027-12-31',
            availability: 'https://schema.org/InStock',
            itemCondition: 'https://schema.org/NewCondition',
            seller: {
              '@type': 'Organization',
              name: 'WellZora Herbal Care',
            },
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: String(product.rating || 5.0),
            reviewCount: String(product.reviews || 10),
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://wellzoraherbalcare.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Shop',
              item: 'https://wellzoraherbalcare.com/shop',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: product.name,
              item: canonical,
            },
          ],
        },
      ],
    };

    return {
      title,
      description,
      canonical,
      ogType: 'product',
      ogImage: productImageUrl,
      jsonLd,
    };
  }, [product, details]);

  usePageSEO(seoData);

  // Reset state on ID change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setQuantity(1);
    setActiveImage(null);
    setAddedToCart(false);
  }, [id]);

  // Related products (4 items excluding current)
  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = products.filter((p) => p.id !== product.id && p.category === product.category);
    const otherProducts = products.filter((p) => p.id !== product.id && p.category !== product.category);
    return [...sameCategory, ...otherProducts].sort((a, b) => {
      if (a.isPackage) return -1;
      if (b.isPackage) return 1;
      return 0;
    }).slice(0, 4);
  }, [product]);

  // If product not found
  if (!product) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-4 py-16 bg-gray-50/50">
        <div className="w-20 h-20 bg-emerald-50 text-primary rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-emerald-100">
          <PackageX className="w-10 h-10" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Product Not Found</h1>
        <p className="text-gray-500 max-w-md mb-8 leading-relaxed text-sm sm:text-base">
          The herbal product you are looking for might have been moved, renamed, or is temporarily out of stock.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 font-medium transition shadow-xs text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Go Back
          </button>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary hover:bg-primary-dark text-white font-medium transition shadow-md shadow-primary/20 text-sm"
          >
            Explore All Products
          </Link>
        </div>
      </div>
    );
  }

  // Pricing & Discounts
  const discount = product.discount || calculateDiscount(product.originalPrice, product.price);
  const hasDiscount = discount > 0 && product.originalPrice && product.originalPrice > product.price;
  const savings = hasDiscount ? product.originalPrice - product.price : 0;

  // Package Items & Thumbnails
  const packageItems = details?.packageIncludes || product.packageItems || [];
  const hasPackageItems = (product.isPackage || packageItems.length > 0);
  const currentPreviewImage = activeImage || product.image;

  const currentQuantity = Math.max(1, Number(quantity) || 1);

  // Quantity Handlers
  const handleDecrement = () => {
    setQuantity((prev) => Math.max(1, (Number(prev) || 1) - 1));
  };

  const handleIncrement = () => {
    setQuantity((prev) => (Number(prev) || 1) + 1);
  };

  const handleQuantityChange = (e) => {
    const val = e.target.value;
    if (val === '') {
      setQuantity('');
      return;
    }
    const parsed = parseInt(val, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      setQuantity(parsed);
    }
  };

  const handleQuantityBlur = () => {
    if (quantity === '' || Number(quantity) < 1) {
      setQuantity(1);
    }
  };

  // Add to cart handler
  const handleAddToCart = () => {
    addToCart(product, currentQuantity);
    setAddedToCart(true);
    setTimeout(() => {
      setAddedToCart(false);
    }, 2000);
  };

  // Share handler
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${product.name} - Wellzora Herbal Care`,
          text: product.description,
          url: window.location.href,
        });
      } catch {
        // user dismissed
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } catch {
        // clipboard access denied or unsupported
      }
    }
  };

  // WhatsApp Order Link
  const whatsappText = `Assalam o Alaikum Wellzora! I would like to order: ${product.name} (${currentQuantity} ${currentQuantity > 1 ? 'units' : 'unit'}) - Total: ${formatPrice(product.price * currentQuantity)}`;
  const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(whatsappText)}`;

  return (
    <div className="bg-gray-50/40 min-h-screen pb-24 sm:pb-16 pt-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 2. Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center flex-wrap gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">
                Home
              </Link>
            </li>
            <li>
              <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
            </li>
            <li>
              <Link to="/shop" className="hover:text-primary transition-colors">
                Shop
              </Link>
            </li>
            {product.category && (
              <>
                <li>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                </li>
                <li>
                  <Link
                    to={`/shop?category=${product.category}`}
                    className="capitalize hover:text-primary transition-colors"
                  >
                    {product.category}
                  </Link>
                </li>
              </>
            )}
            <li>
              <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
            </li>
            <li className="font-semibold text-gray-900 truncate max-w-[180px] sm:max-w-xs" aria-current="page">
              {product.name}
            </li>
          </ol>
        </nav>

        {/* 3. Product Showcase Hero */}
        <div className="bg-white rounded-3xl p-5 sm:p-8 lg:p-10 shadow-sm border border-gray-100/90 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left Column: Visuals (5 cols on lg) */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              {/* Main Image Viewport with Zoom */}
              <div ref={imageContainerRef} className="relative bg-gray-50/80 rounded-2xl overflow-hidden border border-gray-100 group">
                <div className="aspect-square flex items-center justify-center p-4 sm:p-6">
                  <img
                    src={getProductImage(currentPreviewImage)}
                    alt={product.name}
                    className="w-full h-full object-contain transition-transform duration-500 ease-out group-hover:scale-105"
                  />
                </div>

                {/* Badges Overlay */}
                <div className="absolute top-3 left-3 flex flex-col gap-1.5 pointer-events-none">
                  {product.badges && <BadgeGroup badges={product.badges} maxVisible={3} />}
                  {hasDiscount && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-600 text-white shadow-sm">
                      -{discount}% OFF
                    </span>
                  )}
                </div>

                {/* Quick Actions (Wishlist & Share) */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5">
                  <button
                    onClick={() => setIsWishlisted(!isWishlisted)}
                    className={`p-2 rounded-xl transition shadow-sm ${
                      isWishlisted ? 'bg-red-50 text-red-500' : 'bg-white/90 text-gray-600 hover:text-red-500'
                    }`}
                    aria-label="Add to wishlist"
                    title="Add to Wishlist"
                  >
                    <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-red-500' : ''}`} />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-2 bg-white/90 text-gray-600 hover:text-primary rounded-xl transition shadow-sm"
                    aria-label="Share product"
                    title="Share Product"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>

                {copiedLink && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg">
                    Link copied to clipboard!
                  </div>
                )}
              </div>

              {/* Active Preview Switcher (Thumbnails for Package / Included Items) */}
              {hasPackageItems && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-primary" />
                      <span>Package Item Previews ({packageItems.length} Products):</span>
                    </p>
                    {activeImage && (
                      <button
                        onClick={() => setActiveImage(null)}
                        className="text-[11px] text-primary hover:underline font-medium"
                      >
                        Reset to Main Package
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {/* Main package thumbnail */}
                    <button
                      type="button"
                      onClick={() => setActiveImage(null)}
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all p-1 bg-white ${
                        activeImage === null
                          ? 'border-primary ring-2 ring-primary/20 shadow-xs'
                          : 'border-gray-200 hover:border-gray-300 opacity-70 hover:opacity-100'
                      }`}
                      title="Full Package"
                    >
                      <img
                        src={getProductImage(product.image)}
                        alt="Full Package"
                        className="w-full h-full object-contain"
                      />
                    </button>

                    {/* Each package item thumbnail */}
                    {packageItems.map((item, idx) => {
                      const isSelected = activeImage === item.image;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setActiveImage(item.image)}
                          className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all p-1 bg-white ${
                            isSelected
                              ? 'border-primary ring-2 ring-primary/20 shadow-xs'
                              : 'border-gray-200 hover:border-gray-300 opacity-70 hover:opacity-100'
                          }`}
                          title={`${item.name} (${item.size})`}
                        >
                          <img
                            src={getProductImage(item.image)}
                            alt={item.name}
                            className="w-full h-full object-contain"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Trust Guarantee Pills Under Image */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                {TRUST_PILLS.map((pill, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 bg-herbal-50/80 border border-herbal-100 rounded-xl text-xs font-semibold text-herbal-900"
                  >
                    <span className="text-sm">{pill.icon}</span>
                    <span className="truncate">{pill.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Details & Purchase (7 cols on lg) */}
            <div className="lg:col-span-7 flex flex-col justify-between">
              <div>
                {/* Category & Size */}
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 bg-primary/10 text-primary rounded-full uppercase tracking-wider">
                    {product.category}
                  </span>
                  {product.size && (
                    <span className="inline-flex items-center text-xs font-medium px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full border border-gray-200/60">
                      📦 {product.size}
                    </span>
                  )}
                  {product.isPackage && (
                    <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 bg-emerald-600 text-white rounded-full shadow-xs">
                      ⭐ 30-Day Complete Course
                    </span>
                  )}
                </div>

                {/* Product Titles (English & Urdu) */}
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight mb-1">
                  {product.name}
                </h1>
                {product.nameUrdu && (
                  <p
                    className="font-urdu text-xl sm:text-2xl text-primary font-bold mb-3 tracking-wide"
                    dir="rtl"
                  >
                    {product.nameUrdu}
                  </p>
                )}

                {/* Taglines */}
                {(details?.tagline || details?.taglineUrdu) && (
                  <div className="mb-4 pb-3 border-b border-gray-100">
                    {details?.tagline && (
                      <p className="text-sm font-medium text-gray-600">{details.tagline}</p>
                    )}
                    {details?.taglineUrdu && (
                      <p className="font-urdu text-sm text-gray-500 font-medium mt-0.5" dir="rtl">
                        {details.taglineUrdu}
                      </p>
                    )}
                  </div>
                )}

                {/* Rating & Reviews */}
                <div className="mb-5">
                  <StarRating rating={product.rating || 5} reviewsCount={product.reviews || 25} />
                </div>

                {/* Price Block */}
                <div className="bg-emerald-50/40 border border-emerald-100/80 rounded-2xl p-4 sm:p-5 mb-6">
                  <div className="flex items-baseline flex-wrap gap-3">
                    <span className="text-3xl sm:text-4xl font-black text-primary tracking-tight">
                      {formatPrice(product.price)}
                    </span>
                    {hasDiscount && (
                      <span className="text-lg sm:text-xl text-gray-400 line-through font-medium">
                        {formatPrice(product.originalPrice)}
                      </span>
                    )}
                    {hasDiscount && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                        Save {formatPrice(savings)} ({discount}%)
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-emerald-800 font-medium mt-1.5 flex items-center gap-1.5">
                    <span>✓</span>
                    <span>All taxes included • Free delivery across Pakistan</span>
                  </p>
                </div>

                {/* Quick Summary (English & Urdu) */}
                <div className="space-y-2 mb-6">
                  {product.descriptionUrdu && (
                    <div
                      className="font-urdu text-sm sm:text-base text-gray-800 bg-herbal-50/60 p-3.5 rounded-xl border border-herbal-100 leading-relaxed"
                      dir="rtl"
                    >
                      {product.descriptionUrdu}
                    </div>
                  )}
                  {product.description && (
                    <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
                  )}
                </div>

                {/* Purchase Actions (Quantity + Buttons) */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-gray-700">Quantity:</span>
                    <div className="inline-flex items-center bg-gray-100 rounded-xl p-1 border border-gray-200">
                      <button
                        type="button"
                        onClick={handleDecrement}
                        disabled={currentQuantity <= 1}
                        className="w-9 h-9 rounded-lg bg-white flex items-center justify-center text-gray-700 hover:bg-gray-50 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-xs"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={handleQuantityChange}
                        onBlur={handleQuantityBlur}
                        className="w-12 text-center text-sm font-bold text-gray-900 bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        aria-label="Quantity"
                      />
                      <button
                        type="button"
                        onClick={handleIncrement}
                        className="w-9 h-9 rounded-lg bg-white flex items-center justify-center text-gray-700 hover:bg-gray-50 active:scale-95 transition shadow-xs"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <span className="text-xs text-gray-500 font-medium">
                      Subtotal: <strong className="text-gray-900">{formatPrice(product.price * currentQuantity)}</strong>
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    {/* Add to Cart Button */}
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      className={`w-full py-3.5 px-6 rounded-xl font-bold flex items-center justify-center gap-2.5 transition-all shadow-md active:scale-[0.98] ${
                        addedToCart
                          ? 'bg-emerald-700 text-white shadow-emerald-700/20'
                          : 'bg-primary hover:bg-primary-dark text-white shadow-primary/30'
                      }`}
                    >
                      {addedToCart ? (
                        <>
                          <Check className="w-5 h-5 animate-bounce" />
                          <span>✓ Added to Cart!</span>
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="w-5 h-5" />
                          <span>Add to Cart</span>
                        </>
                      )}
                    </button>

                    {/* Order via WhatsApp Button */}
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3.5 px-6 rounded-xl font-bold bg-[#25D366] hover:bg-[#20bd5a] text-white flex items-center justify-center gap-2.5 transition-all shadow-md shadow-green-500/20 active:scale-[0.98]"
                    >
                      <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                      </svg>
                      <span>Order on WhatsApp (COD)</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Delivery & Service Guarantees Card */}
              <div className="bg-gray-50/80 rounded-2xl p-4 border border-gray-200/70">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white border border-gray-200/80 flex items-center justify-center flex-shrink-0 text-primary shadow-xs">
                      <Truck className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Cash on Delivery</p>
                      <p className="text-[11px] text-gray-500">All Pakistan coverage</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white border border-gray-200/80 flex items-center justify-center flex-shrink-0 text-primary shadow-xs">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Fast & Discreet</p>
                      <p className="text-[11px] text-gray-500">2–4 days delivery</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white border border-gray-200/80 flex items-center justify-center flex-shrink-0 text-primary shadow-xs">
                      <ShieldCheck className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">100% Authentic</p>
                      <p className="text-[11px] text-gray-500">Factory sealed guarantee</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Bilingual Tabbed Information Section */}
        <ProductTabs
          product={product}
          details={details}
          packageItems={packageItems}
          onPreviewImage={(image) => {
            setActiveImage(image);
            imageContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
        />

        {/* 5. Related Products Section */}
        {relatedProducts.length > 0 && (
          <div className="mb-14">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">
                  Related Herbal Products
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                  Customers who viewed this product also explored
                </p>
              </div>
              <Link
                to="/shop"
                className="text-xs sm:text-sm font-semibold text-primary hover:underline flex items-center gap-1"
              >
                <span>View Full Catalog</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relProduct) => (
                <ProductCard key={relProduct.id} product={relProduct} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 6. Mobile Sticky Bottom Bar (sm:hidden) */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 px-4 py-2.5 shadow-2xl flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-11 h-11 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden p-0.5">
            <img
              src={getProductImage(product.image)}
              alt={product.name}
              className="w-full h-full object-contain"
            />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-gray-900 truncate">{product.name}</p>
            <p className="text-xs font-extrabold text-primary">{formatPrice(product.price)}</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={handleAddToCart}
            className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md active:scale-95 transition ${
              addedToCart
                ? 'bg-emerald-700 text-white'
                : 'bg-primary hover:bg-primary-dark text-white'
            }`}
          >
            {addedToCart ? (
              <>
                <Check className="w-3.5 h-3.5" />
                <span>Added</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-3.5 h-3.5" />
                <span>Add to Cart</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
